import LoadingButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { Chip, ChipProps, Skeleton } from '@mui/material';
import isPast from 'date-fns/isPast';
import isToday from 'date-fns/isToday';
import { useGetSignedInUser } from '../../hooks/useGetSignedInUser';
import { ApplyModule } from '../../store/features/apply/types';
import { StudentProfile, BaseStudentApplication } from '../../types/profile';
import { ApplyItem } from './types';

interface ApplyButtonProps extends LoadingButtonProps {
  applyItem?: ApplyItem | ApplyItem[];
  module: ApplyModule;
  ChipProps?: ChipProps;
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({ applyItem, module, ChipProps, ...props }) => {
  const { currentData: userData, isFetching } = useGetSignedInUser();

  // Show loading button in case user is logging in
  if (isFetching) return <Skeleton variant="rectangular" height="2.75rem" width="7.5rem" />;

  const renderApplyBtn = (): React.ReactElement => {
    return (
      <LoadingButton variant="contained" type="button" {...props}>
        {userData?.user ? 'Apply Now' : 'Sign in to Apply'}
      </LoadingButton>
    );
  };

  /**
   * Render apply button in case "applyItem" is not provided
   * This may happen in case of mass apply which allows multiple application at once
   */
  if (!applyItem || Array.isArray(applyItem)) return renderApplyBtn();

  const getApplicationStatus = (): false | BaseStudentApplication['is_approved'] => {
    const appliesModule: keyof StudentProfile['applies'] =
      module === 'institute_programs' ? 'programs' : 'scholarships';
    const applications = userData?.user && userData.user.applies[appliesModule];
    if (applications) {
      const idx = applications.findIndex(
        (application) => application.applicable_id.toString() === applyItem.id.toString()
      );
      if (idx !== -1) {
        return applications[idx].is_approved;
      }
    }
    return false;
  };
  const applicationStatus = getApplicationStatus();
  const isApplied = applicationStatus !== false;
  const isPending = applicationStatus.toString() === '0';
  const isApproved = applicationStatus.toString() === '1';
  const isDeadlinePassed = isPast(new Date(applyItem.deadline)) && !isToday(new Date(applyItem.deadline));
  const isInapplicable = !applyItem.is_applicable || !applyItem.is_published;

  const renderChip = () => {
    return (
      <Chip
        label={isApplied ? (isPending ? 'Applied' : isApproved ? 'Approved' : 'Denied') : 'Deadline Passed'}
        variant="light"
        sx={{ fontSize: '0.925rem' }}
        color={isApplied && isPending ? 'primary' : isApproved ? 'success' : 'error'}
        {...ChipProps}
      />
    );
  };

  /**
   * Render UI based on applicable status, deadline and applied status
   */
  return isApplied || isDeadlinePassed ? renderChip() : !isInapplicable ? renderApplyBtn() : null;
};
